import { makeAutoObservable, runInAction } from "mobx";
import * as api from "../api";

class MyMembersStore {

    marketplaceId = null
    sellerId = null
    endUserSeasonTickets = []
    loading = true

    constructor() {
        makeAutoObservable(this)
    }

    get members() {
        // unique members based on endUserId
        return Array.from(new Set(this.endUserSeasonTickets.map(ticket => ticket.endUserId)))
            .map(endUserId => this.endUserSeasonTickets.find(ticket => ticket.endUserId === endUserId))
    }

    async fetchData(options = {}) {
        const { showLoader = true } = options
        this.loading = showLoader
        try {
            const response = await api.getMyMembers({ marketplaceId: this.marketplaceId, sellerId: this.sellerId, })
            runInAction(() => {
                this.endUserSeasonTickets = response.data
                    .map(x => {
                        const { _id, name, endUser, status, createdAt, cancelDate, iterationsCompleted, price, hrc, startDate, comment, lastPaymentFailedAt, failedPaymentAttempts, errorText, isProfileComplete, familyRole, parentSubscriptionId, reportGroup, seasonTicket, printedAt, printCount } = x
                        const { email = "", fname = "", lname = "", phone = "", lastParticipationDate = "" } = endUser || {}
                        // A child has no endUser row of their own — the parent holds the account —
                        // so their name lives on the subscription as `memberName`. Reading only
                        // endUser left every child in the grid as "----", including the 48 of 146
                        // on staging whose names are perfectly well filled in. The remaining ones
                        // are genuinely unnamed (profile not completed) and still fall through.
                        const ownName = `${fname} ${lname}`.trim()
                        return {
                            _id,
                            endUserId: endUser?._id,
                            fullName: ownName || (x.memberName || '').trim(),
                            phone,
                            email,
                            createdAt,
                            cancelDate,
                            startDate,
                            iterationsCompleted,
                            price,
                            status,
                            name,
                            hrc,
                            comment,
                            lastParticipationDate,
                            lastPaymentFailedAt,
                            failedPaymentAttempts,
                            errorText,
                            isProfileComplete,
                            familyRole: familyRole || null,
                            parentSubscriptionId: parentSubscriptionId || null,
                            // The subscription type's report group (nimi-backend#1324): flat on the
                            // record, or on a populated seasonTicket.
                            reportGroup: reportGroup ?? seasonTicket?.reportGroup ?? null,
                            // nimi-backend#1513. Left undefined when absent, never defaulted to
                            // null/0: absence is what says "never printed", and a 0 would claim
                            // the card was printed zero times.
                            printedAt,
                            printCount,
                        }
                    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            })
        } catch (error) {
            console.error(error)
            throw error
        } finally {
            this.loading = false
        }
    }

    async refreshData() {
        return this.fetchData({ showLoader: false })
    }

    init(marketplaceId, sellerId) {
        if (this.endUserSeasonTickets.length) {
            return
        }
        this.marketplaceId = marketplaceId
        this.sellerId = sellerId
        return this.fetchData()
    }

    // Computed field that reduces all endUserSeasonTickets names
    get subscriptionNames() {
        return Array.from(new Set(this.endUserSeasonTickets.map(ticket => ticket.name)));
    }
}

export default new MyMembersStore();